import { homerunnerApiBaseUrl } from "../../hr";
import { okOrThrow } from "../../common/http";
import { propertyQueryKey, propertyQueryOptions } from "../../common/property-query";
import { AvailabilityDataRecord, AvailabilityEntry, DateKey, Property } from "../../types";
import { useQuery } from "@tanstack/react-query";
import z from "zod";

// =====================================================
// TODO:Set to false to use real API instead of mock data
const USE_MOCK_DATA = false;
// =====================================================

// Filter schema for calendar API parameters
export const calendarFilterSchema = z.object({
    feed_id: z.number().int().describe('Filter property by feed'),
    platform: z.string().nullish().describe('Filter property by platform'),
    property: z.string().describe('Filter property by property slug'),
}).describe('Data filter options for property');

export type CalendarFilter = z.infer<typeof calendarFilterSchema>;

// --- Mock data generator ---

function toDateKey(d: Date): DateKey {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}` as DateKey
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d)
  r.setDate(r.getDate() + n)
  return r
}

function generateMockAvailability(): AvailabilityDataRecord {
  const data: AvailabilityDataRecord = {} as AvailabilityDataRecord
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  // Generate 6 months of data from today
  for (let i = 0; i < 180; i++) {
    const date = addDays(today, i)
    const key = toDateKey(date)
    const dayOfMonth = date.getDate()

    // Default: fully available
    let entry: AvailabilityEntry = {
      date: key,
      available: 1,
      checkin: 1,
      checkout: 1,
      min_stay: 2,
      max_stay: 14,
      price: 120 + Math.round(Math.sin(i * 0.3) * 40), // varying prices ~$80-$160
    }

    // --- Past dates (already handled by calendar-utils, but mark explicitly) ---
    // (none needed, today offset starts at 0)

    // --- BOOKED BLOCK: days 5-9 from today (a 5-night booking) ---
    // Day 5: checkout booked (previous guest leaves) + checkin booked (new guest arrives)
    // Days 6-8: fully booked (not available, no checkin/checkout)
    // Day 9: checkout booked (guest leaves)
    if (i >= 5 && i <= 9) {
      if (i === 5) {
        // Turnover day: previous checkout + new checkin
        entry = { ...entry, available: 1, checkin: 2, checkout: 2, price: 145 }
      } else if (i === 9) {
        // Guest checks out, next guest can check in
        entry = { ...entry, available: 1, checkin: 1, checkout: 2, price: 130 }
      } else {
        // Mid-stay: fully occupied
        entry = { ...entry, available: 0, checkin: 0, checkout: 0, price: 135 }
      }
    }

    // --- BLOCKED BLOCK: days 15-17 (owner block / maintenance) ---
    if (i >= 15 && i <= 17) {
      entry = { ...entry, available: 0, checkin: 0, checkout: 0, price: 0 }
    }

    // --- CHECKIN-ONLY days: day 18 (checkout blocked but checkin available) ---
    if (i === 18) {
      entry = { ...entry, available: 1, checkin: 1, checkout: 0, min_stay: 3, price: 155 }
    }

    // --- CHECKOUT-ONLY day: day 14 (checkin blocked but checkout available) ---
    if (i === 14) {
      entry = { ...entry, available: 1, checkin: 0, checkout: 1, price: 110 }
    }

    // --- PASS-THROUGH days: days 25-27 (can stay but no checkin/checkout) ---
    if (i >= 25 && i <= 27) {
      entry = { ...entry, available: 1, checkin: 0, checkout: 0, price: 100 }
    }

    // --- HIGH MIN-STAY: days 30-40 (min stay 5 nights) ---
    if (i >= 30 && i <= 40) {
      entry = { ...entry, min_stay: 5, price: 175 }
    }

    // --- ANOTHER BOOKED BLOCK: days 45-50 ---
    if (i >= 45 && i <= 50) {
      if (i === 45) {
        entry = { ...entry, available: 1, checkin: 2, checkout: 1, price: 140 }
      } else if (i === 50) {
        entry = { ...entry, available: 1, checkin: 1, checkout: 2, price: 140 }
      } else {
        entry = { ...entry, available: 0, checkin: 0, checkout: 0, price: 140 }
      }
    }

    // --- TRAPPED DATE: day 53 (available but surrounded by unavailable) ---
    if (i === 52 || i === 54) {
      entry = { ...entry, available: 0, checkin: 0, checkout: 0, price: 0 }
    }
    if (i === 53) {
      entry = { ...entry, available: 1, checkin: 1, checkout: 1, min_stay: 3, price: 130 }
    }

    // --- WEEKEND PREMIUM: Fri+Sat get higher prices ---
    const dow = date.getDay()
    if (dow === 5 || dow === 6) {
      entry = { ...entry, price: entry.price + 30 }
    }

    // --- SINGLE-NIGHT GAP (trapped): day 60 between two bookings ---
    if (i === 59 || i === 61) {
      entry = { ...entry, available: 0, checkin: 0, checkout: 0, price: 0 }
    }
    if (i === 60) {
      entry = { ...entry, available: 1, checkin: 1, checkout: 1, min_stay: 2, price: 115 }
    }

    // --- MIXED BLOCK: days 70-80 with varying states ---
    if (i === 70) {
      // Checkout booked (someone leaving), checkin available
      entry = { ...entry, available: 1, checkin: 1, checkout: 2, price: 160 }
    }
    if (i >= 71 && i <= 74) {
      // Open availability with higher min stay
      entry = { ...entry, available: 1, checkin: 1, checkout: 1, min_stay: 4, price: 180 }
    }
    if (i >= 75 && i <= 78) {
      // Booked
      entry = { ...entry, available: 0, checkin: 0, checkout: 0, price: 180 }
    }
    if (i === 79) {
      // Checkout available, checkin blocked
      entry = { ...entry, available: 1, checkin: 0, checkout: 1, price: 160 }
    }
    if (i === 80) {
      // Checkin available again
      entry = { ...entry, available: 1, checkin: 1, checkout: 1, min_stay: 2, price: 150 }
    }

    // --- NO DATA GAP: skip days 90-92 (no entry = unavailable in calendar-utils) ---
    if (i >= 90 && i <= 92) {
      continue
    }

    // --- LOW SEASON: days 100-130 with lower prices ---
    if (i >= 100 && i <= 130) {
      entry = { ...entry, price: 80 + Math.round(Math.random() * 20) }
    }

    // --- PEAK SEASON: days 140-160 with high prices and 7-night min ---
    if (i >= 140 && i <= 160) {
      entry = { ...entry, min_stay: 7, max_stay: 21, price: 220 + Math.round(Math.random() * 60) }
    }

    data[key] = entry
  }

  return data
}

const MOCK_PROPERTY: Property = {
  id: 147,
  uuid: 'f1ff8bf4-3b58-4123-9f62-c6cd040c67cf',
  platform_id: 8,
  source: 'ownerrez',
  source_id: '418990',
  airbnb_id: null,
  homeaway_id: null,
  city_id: 30,
  type_id: 47,
  category: 'standalone',
  parent_id: null,
  name: 'Seaside Villa',
  title: 'Beautiful Seaside Villa with Ocean View',
  status: 1,
  floor_count: null,
  area_size: null,
  area_size_unit: 'SQUARE_FEET',
  bedroom_count: 2,
  bathroom_count: '1.0',
  bed_count: 5,
  base_guest_count: 1,
  maximum_guest_count: 5,
  unit_count: 1,
  minimum_stay: 2,
  maximum_stay: 14,
  base_daily_rate: 120,
  extra_guest_fee: 0,
  cleaning_fee_amount: 0,
  security_deposit_amount: 0,
  taxation_rate: 0,
  percent_upon_reservation: 0,
  default_checkin_at: '17',
  default_checkout_at: '10',
  advance_notice_hours: null,
  preparation_days: null,
  booking_window: 365,
  full_payment_timing: null,
  accept_instant_booking: 0,
  accept_booking_request: 0,
  reservation_method: 'inquiry',
  reservation_allow_coupon: 0,
  rental_condition: null,
  cancellation_policy: '',
  listed_at: null,
  currency_code: 'EUR',
  pets_allowed: 0,
  web_url: '',
  source_url: '',
  picture_url: '',
  availability_calendar_url: null,
  panoramic_data_url: null,
  is_featured: 0,
  address_1: '',
  address_2: null,
  street: null,
  state: '',
  zip: '',
  country: null,
  country_code: 'US',
  full_address: null,
  public_address: null,
  longitude: '0',
  latitude: '0',
  created_at: '',
  updated_at: '',
  last_synced_at: '',
  monthly_price_factor: 0.75,
  weekly_price_factor: 0.85,
  amenities_not_included: null,
  timezone: 'Europe/Copenhagen',
  additional_info: [],
  reservation_target: 'hosted',
  payment_target: 'none',
  payment_provider: [],
  inquiry_requires_payment: false,
  max_stays: -1,
}

// --- Real API fetchers ---

export async function fetchAvailability(filter: CalendarFilter) {
    const url = new URL(`${homerunnerApiBaseUrl()}/public-api/v1/property-calendar`)
    Object.entries(filter).forEach(([key, value]) => {
      if (value !== undefined && value !== null) url.searchParams.append(key, String(value))
    })
    const res = await fetch(url.toString())
    await okOrThrow(res, 'Failed to fetch property availability')
    const data = await res.json()

    return data.result as AvailabilityDataRecord;
  }

export function useFetchAvailability(filter: CalendarFilter) {
    return useQuery({
        queryKey: ['availability', filter],
        queryFn: () => USE_MOCK_DATA
          ? Promise.resolve(generateMockAvailability())
          : fetchAvailability(filter),
        refetchInterval: 2 * 60 * 1000, // refetch every 2 minutes
        refetchOnWindowFocus: true,
        refetchOnMount: true,
    })
}

export function useFetchProperty(filter: CalendarFilter) {
    // The guards (error-envelope throw, 15s bound, retry: 1) live in
    // common/property-query.ts because usePropertyCustomFields shares this
    // exact key — see the header there before adding a second queryFn.
    return useQuery(USE_MOCK_DATA
      ? {
          queryKey: propertyQueryKey(filter),
          queryFn: () => Promise.resolve({ status: 'ok' as const, result: MOCK_PROPERTY }),
        }
      : propertyQueryOptions<Property>(filter))
}
